import { c as _c } from "react/compiler-runtime";
import { UserCircle } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, Field, FieldLayout, hasConflicts, LinkToEntity, renderBadge } from "@/src/components/cards/common";
import PatientCardEditable from "@/src/components/cards/patient-card/patient-card-editable";
import ErrorAlert from "@/src/components/error-alert";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { useUser } from "@/src/context/user-context";
import { logger } from "@/src/lib/logging/logger";
import { canAccessInbox, canWritePatient } from "@/src/lib/rbac";
import { tryGetEhrSystemId, tryGetMrn } from "@/src/lib/resolve-ehr-ids";
import { Patient } from "@/src/models/patient";
export const PATIENT_FIELDS: Field[] = [{
  key: "familyName",
  label: "Family Name",
  comparable: true,
  required: true
}, {
  key: "givenName",
  label: "Given Name",
  comparable: true,
  required: true
}, {
  key: "middleName",
  label: "Middle Name",
  comparable: true
}, {
  key: "dateOfBirth",
  label: "Date of Birth",
  type: "date",
  comparable: true
}, {
  key: "gender",
  label: "Gender",
  comparable: true
}, {
  key: "race",
  label: "Race",
  comparable: false
}, {
  key: "ethnicity",
  label: "Ethnicity",
  comparable: false
}, {
  key: "language",
  label: "Language",
  comparable: false
}, {
  key: "ehrId",
  label: "EHR ID",
  comparable: false,
  getValue: (p: Patient) => tryGetMrn(p)?.replace(/^0+/, "") || tryGetEhrSystemId(p)
}, {
  key: "homeAddress.line1",
  label: "Address",
  comparable: true,
  colSpan: 2,
  sectionHeader: "Address"
}, {
  key: "homeAddress.line2",
  label: "Address 2",
  comparable: true
}, {
  key: "homeAddress.city",
  label: "City",
  comparable: true
}, {
  key: "homeAddress.state",
  label: "State",
  comparable: true
}, {
  key: "homeAddress.postalCode",
  label: "Zip Code",
  comparable: true
}, {
  key: "homePhoneNumber",
  label: "Home Phone",
  type: "tel",
  comparable: true
}, {
  key: "mobilePhoneNumber",
  label: "Mobile Phone",
  type: "tel",
  comparable: true
}, {
  key: "personalEmail",
  label: "Personal Email",
  comparable: true
}, {
  key: "type",
  label: "Type",
  comparable: false
}];
export default function PatientCard(t0) {
  const $ = _c(57);
  const {
    patient,
    compareMode: t1,
    secondaryPatient: t2,
    alwaysShowLink: t3
  } = t0;
  const compareMode = t1 === undefined ? false : t1;
  const secondaryPatient = t2 === undefined ? null : t2;
  const alwaysShowLink = t3 === undefined ? false : t3;
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const {
    group,
    roles
  } = useUser();
  let t4;
  if ($[0] !== roles) {
    t4 = canWritePatient(roles);
    $[0] = roles;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const canWrite = t4;
  let t5;
  if ($[2] !== roles) {
    t5 = canAccessInbox(roles);
    $[2] = roles;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const canContact = t5;
  let t6;
  if ($[4] !== group) {
    t6 = f => {
      if (f.key !== "ehrId") {
        return f;
      }
      return {
        ...f,
        getValue: p => tryGetMrn(p, group)?.replace(/^0+/, "") || tryGetEhrSystemId(p, group)
      };
    };
    $[4] = group;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const fields = PATIENT_FIELDS.map(t6);
  let t7;
  let t8;
  if ($[6] !== patient || $[7] !== secondaryPatient) {
    t7 = () => {
      logger.info("Patient in patient card", {
        patient,
        secondaryPatient
      });
    };
    t8 = [patient, secondaryPatient];
    $[6] = patient;
    $[7] = secondaryPatient;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let hasAnyConflicts = false;
  if (compareMode && secondaryPatient) {
    hasAnyConflicts = hasConflicts(fields, patient, secondaryPatient);
  }
  let t9;
  if ($[10] !== isEditing) {
    t9 = () => {
      setIsEditing(!isEditing);
    };
    $[10] = isEditing;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleEditToggle = t9;
  if (isEditing) {
    return <PatientCardEditable patient={patient} onClose={handleEditToggle} compareMode={compareMode} secondaryObject={secondaryPatient} fields={fields} />;
  }
  const t10 = `/patients/${patient.displayId}`;
  let t11;
  if ($[12] !== handleEditToggle || $[13] !== patient.id || $[14] !== t10) {
    t11 = createCardActionsProps({
      id: patient.id,
      label: "Patient ID",
      href: t10,
      customProps: [{
        label: "Edit Patient",
        onClick: handleEditToggle
      }]
    });
    $[12] = handleEditToggle;
    $[13] = patient.id;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const cardActionsProps = t11;
  const T0 = ContextMenu;
  const T1 = ContextMenuTrigger;
  const t12 = true;
  const T2 = Card;
  const t13 = "w-full focus:outline-none focus:ring-0";
  const t14 = 0;
  const T3 = CardHeader;
  const t15 = "flex items-center justify-between";
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="flex items-center"><UserCircle className="h-6 w-6 mr-2" /><CardTitle className="text-md font-semibold">Patient Information</CardTitle></div>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  const t17 = "ml-auto flex items-center gap-2";
  const t18 = patient && renderBadge(compareMode, secondaryPatient, hasAnyConflicts);
  let t19;
  if ($[17] !== alwaysShowLink || $[18] !== pathname || $[19] !== patient) {
    t19 = patient && (alwaysShowLink || !pathname?.startsWith(`/patients/${patient.displayId}`)) ? <LinkToEntity href={`/patients/${patient.displayId}`} /> : null;
    $[17] = alwaysShowLink;
    $[18] = pathname;
    $[19] = patient;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] !== canContact || $[22] !== canWrite || $[23] !== cardActionsProps || $[24] !== patient || $[25] !== router) {
    t20 = canWrite && patient && <EntityDropdownMenu cardActionsProps={cardActionsProps} customProps={canContact ? [{
      label: "Contact Patient",
      onClick: () => router.push(`/inbox/${patient.displayId}`)
    }] : []} />;
    $[21] = canContact;
    $[22] = canWrite;
    $[23] = cardActionsProps;
    $[24] = patient;
    $[25] = router;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== t18 || $[28] !== t19 || $[29] !== t20) {
    t21 = <div className={t17}>{t18}{t19}{t20}</div>;
    $[27] = t18;
    $[28] = t19;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== t16 || $[32] !== t21) {
    t22 = <div className={t15}>{t16}{t21}</div>;
    $[31] = t16;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== T3 || $[35] !== t22) {
    t23 = <T3>{t22}</T3>;
    $[34] = T3;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== compareMode || $[38] !== fields || $[39] !== patient || $[40] !== secondaryPatient) {
    t24 = <CardContent>{!patient ? <ErrorAlert message="Patient not found" compact={true} /> : <FieldLayout fields={fields} patient={patient} secondaryObject={secondaryPatient} compareMode={compareMode} />}</CardContent>;
    $[37] = compareMode;
    $[38] = fields;
    $[39] = patient;
    $[40] = secondaryPatient;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== T2 || $[43] !== t23 || $[44] !== t24) {
    t25 = <T2 className={t13} tabIndex={t14}>{t23}{t24}</T2>;
    $[42] = T2;
    $[43] = t23;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== T1 || $[47] !== t25) {
    t26 = <T1 asChild={t12}>{t25}</T1>;
    $[46] = T1;
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== canWrite || $[50] !== cardActionsProps || $[51] !== patient) {
    t27 = canWrite && patient && <CardActions {...cardActionsProps} isContextMenu={true} />;
    $[49] = canWrite;
    $[50] = cardActionsProps;
    $[51] = patient;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== T0 || $[54] !== t26 || $[55] !== t27) {
    t28 = <T0>{t26}{t27}</T0>;
    $[53] = T0;
    $[54] = t26;
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  return t28;
}
function GridItemSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="inline-block align-text-bottom h-4 w-[100px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function PatientCardSkeleton() {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CardHeader><div className="flex items-center"><UserCircle className="h-6 w-6 mr-2" /><CardTitle className="text-md font-semibold">Patient Information</CardTitle></div></CardHeader>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="font-semibold">Last Name:</span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div>{t1} <GridItemSkeleton /></div>;
    t3 = <span className="font-semibold">Given Name:</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div>{t3} <GridItemSkeleton /></div>;
    t5 = <span className="font-semibold">Date of Birth:</span>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div>{t5} <GridItemSkeleton /></div>;
    t7 = <span className="font-semibold">Gender:</span>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div>{t7} <GridItemSkeleton /></div>;
    t9 = <span className="font-semibold">Email:</span>;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Card className="w-full">{t0}<CardContent><div className="grid grid-cols-3 gap-2 text-sm">{t2}{t4}{t6}{t8}<div>{t9} <GridItemSkeleton /></div></div></CardContent></Card>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  return t10;
}